import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatEUR, formatDate } from "@/lib/api";
import { Users, Clock, FileCheck2, Receipt, FlaskConical } from "lucide-react";
import { useOutletContext, Link } from "react-router-dom";

const Kpi = ({ icon: Icon, label, value, hint, accent = "#B84A2B", testid }) => (
  <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
        <Icon size={14} />
      </div>
    </div>
    <div className="mt-3 text-2xl font-bold tracking-tight">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function ResellerDashboard() {
  const api = useApi();
  const me = useOutletContext();
  const { data } = useQuery({
    queryKey: ["reseller-dashboard"],
    queryFn: async () => (await api.get("/reseller/dashboard")).data,
  });

  if (!me?.reseller) {
    return (
      <div className="bg-white border border-[#E2DDD5] rounded-xl p-10 text-center">
        <h2 className="text-xl font-semibold">Benvenuto nel portale GoWeb</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Completi la Sua anagrafica per iniziare l'onboarding come rivenditore.</p>
        <Link to="/portal/profile"><button className="mt-5 bg-[#B84A2B] hover:bg-[#983C21] text-white px-5 py-2 rounded-lg" data-testid="btn-start-onboarding">Inizia l'anagrafica →</button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Benvenuto, {me?.reseller?.anagrafica?.nome || me?.user?.email}</h1>
        <p className="text-sm text-slate-500 mt-1">Panoramica della Sua attività come rivenditore GoWeb.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={Users} label="Clienti attivi" value={data?.active ?? 0} hint={`${formatEUR(data?.commission_active_total)} di provvigioni maturate`} testid="kpi-active" accent="#206342" />
        <Kpi icon={FlaskConical} label="Clienti in demo" value={data?.demo ?? 0} hint="nessuna provvigione finché non conferma" testid="kpi-demo" accent="#2B6CB0" />
        <Kpi icon={Clock} label="In scadenza (30gg)" value={data?.expiring ?? 0} testid="kpi-expiring" accent="#C69214" />
        <Kpi icon={FileCheck2} label="Licenze in approvazione" value={data?.pending ?? 0} testid="kpi-pending" accent="#B84A2B" />
        <Kpi icon={Receipt} label="Provvigioni del mese" value={formatEUR(data?.commission_month)} hint={`${formatEUR(data?.commission_pending)} ancora da liquidare`} testid="kpi-month" accent="#1E2A38" />
      </div>
      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500">Codice rivenditore</div>
        <div className="font-mono text-xl mt-1">{data?.code}</div>
        <div className="text-sm text-slate-500 mt-2">I Suoi clienti inseriscono questo codice al rinnovo direttamente nell'app acquistata per riconoscerLe le provvigioni.</div>
      </div>
    </div>
  );
}
